import DOMPurify from "isomorphic-dompurify";
import styles from "./richText.module.css";

const ALLOWED_TAGS = [
    "p", "br",
    "strong", "b", "em", "i", "u", "s", "strike",
    "h2", "h3", "h4",
    "ul", "ol", "li",
    "blockquote",
    "a",
    "span",
    "img",
];

// `style` carries the editor's text-align output (left/center/right/justify).
// `src` + `alt` enable inline images uploaded through the admin RichText
// image button. DOMPurify itself filters dangerous CSS, so leaving style open
// here is safe.
const ALLOWED_ATTR = ["href", "target", "rel", "src", "alt", "style"];

/** Walk the saved HTML and prepend a sensible protocol on <a href="…">
 *  values that don't carry one. Without this DOMPurify drops the href
 *  entirely (its ALLOWED_URI_REGEXP requires http(s)/mailto/tel or a
 *  leading / or #), so a link the admin entered as "gisperg.wine" would
 *  render as visible underlined text that goes nowhere. The newer admin
 *  prompt normalises on input — this also covers entries saved before
 *  that fix landed. */
function normaliseAnchorHrefs(html) {
    return html.replace(
        /(<a\b[^>]*\shref\s*=\s*)(['"])(.*?)\2/gi,
        (match, lead, quote, href) => {
            const trimmed = href.trim();
            if (
                !trimmed ||
                /^(https?:|mailto:|tel:|\/|#|javascript:|data:)/i.test(trimmed) ||
                trimmed.startsWith('//')
            ) {
                return match;
            }
            let next;
            if (/^[\w.+-]+@[\w.-]+\.[a-z]{2,}$/i.test(trimmed)) {
                next = `mailto:${trimmed}`;
            } else if (/^[+\d][\d\s/().-]{4,}$/.test(trimmed)) {
                next = `tel:${trimmed.replace(/[^+\d]/g, '')}`;
            } else {
                next = `https://${trimmed}`;
            }
            return `${lead}${quote}${next}${quote}`;
        }
    );
}

function sanitize(html) {
    if (!html) return "";
    return DOMPurify.sanitize(normaliseAnchorHrefs(html), {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOWED_URI_REGEXP: /^(https?:|mailto:|tel:|\/|#)/i,
    });
}

/**
 * Renders sanitized HTML content from the admin's rich-text editor.
 * Forwards extra props (className, style, etc.) to the wrapping element.
 *
 * Falls back to "div" so block-level rich content (lists, headings) is valid;
 * pass `as="span"` for inline contexts.
 */
export default function RichText({ html, as: Tag = "div", className, ...rest }) {
    if (!html) return null;
    const clean = sanitize(html);
    const composedClassName = [styles.rich, className].filter(Boolean).join(" ");
    return (
        <Tag
            {...rest}
            className={composedClassName}
            dangerouslySetInnerHTML={{ __html: clean }}
        />
    );
}

/**
 * Strip all HTML tags, returning a plain-text excerpt. Used for list previews
 * where the full markup doesn't fit (e.g. blog teasers).
 */
export function stripHtml(html, max) {
    if (!html) return "";
    const cleaned = DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    const text = cleaned.replace(/\s+/g, " ").trim();
    if (typeof max === "number" && text.length > max) {
        return `${text.slice(0, max).trimEnd()}…`;
    }
    return text;
}
